import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../context/PermissionsContext';
import { AlertTriangle, Infinity } from 'lucide-react';
import './LimitBadge.css';

/**
 * LimitBadge Component
 * Shows the remaining quota for a limited feature
 * 
 * Usage:
 * <LimitBadge limitCode="limit_offers" currentCount={userOffersCount} />
 */
const LimitBadge = ({ 
  limitCode, 
  currentCount = 0, 
  showTotal = true,
  size = 'small' // 'small' | 'medium' | 'large'
}) => {
  const { t } = useTranslation();
  const { getLimit, getRemainingQuota, hasActiveSubscription } = usePermissions();

  if (!hasActiveSubscription) {
    return null;
  }

  const limit = getLimit(limitCode);
  const remaining = getRemainingQuota(limitCode, currentCount);
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 100 : (remaining / limit) * 100;
  const isCritical = !isUnlimited && remaining <= Math.ceil(limit * 0.2);
  const isWarning = !isUnlimited && remaining <= Math.ceil(limit * 0.4);

  if (limit === 0) {
    return null;
  }

  return (
    <div className={`limit-badge size-${size} ${isCritical ? 'critical' : isWarning ? 'warning' : ''}`}>
      {isCritical && <AlertTriangle size={size === 'small' ? 12 : 16} />}
      
      {isUnlimited ? (
        <span className="limit-value">
          <Infinity size={size === 'small' ? 14 : 16} />
          <span>{t('permissions.unlimited')}</span>
        </span>
      ) : (
        <>
          <span className="limit-value">
            <span className="current">{remaining}</span>
            {showTotal && (
              <>
                <span className="separator">/</span>
                <span className="total">{limit}</span>
              </>
            )}
          </span>
          <div className="limit-progress">
            <div 
              className="limit-progress-bar" 
              style={{ width: `${percentage}%` }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default LimitBadge;
